import type { AnalyticsDto } from '@telestore/contracts';
import { useQuery } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { api, ApiError } from '../lib/api.js';
import { useSession } from '../lib/session.js';
import { Empty, ErrorBox, Money, Panel, Row, Rows, Spinner, Stat } from '../components/ui.js';

/**
 * Analytics (CLAUDE.md §3 `analytics`): revenue, SKU and COD reports.
 *
 * Four questions a Cambodian fashion merchant actually asks, in order:
 * how much did I make, what sold, how much cash is still with the courier,
 * and which channel sent it. Nothing else — a dashboard nobody reads is worse
 * than no dashboard.
 */
export function AnalyticsPage() {
  const { shopId, locale, t } = useSession();
  const [days, setDays] = useState(30);

  const range = useMemo(() => {
    const to = new Date();
    const from = new Date(to.getTime() - days * 86_400_000);
    return { from: from.toISOString(), to: to.toISOString() };
  }, [days]);

  const analytics = useQuery({
    queryKey: ['analytics', shopId, days],
    queryFn: () =>
      api.analytics(shopId!, { ...range, granularity: days > 45 ? 'week' : 'day' }),
    enabled: Boolean(shopId),
    retry: false,
  });

  if (analytics.isPending) return <Spinner label={t('common.loading')} />;
  if (analytics.isError) {
    const error = analytics.error;
    if (error instanceof ApiError && error.code === 'plan_required') {
      return <Empty title={t('admin.upgradeToUnlock')} hint={error.message} />;
    }
    return <ErrorBox message={error instanceof ApiError ? error.message : t('errors.internal')} />;
  }

  const data = analytics.data;
  const currency = data.currency;

  return (
    <div className="flex flex-col gap-4">
      <nav className="flex gap-1">
        {[
          { value: 7, label: t('growth.range7') },
          { value: 30, label: t('growth.range30') },
          { value: 90, label: t('growth.range90') },
        ].map((entry) => (
          <button
            key={entry.value}
            type="button"
            onClick={() => setDays(entry.value)}
            className="rounded-full px-3 py-1.5 text-sm font-medium"
            style={{
              background: days === entry.value ? 'var(--color-brand-soft)' : 'transparent',
              color: days === entry.value ? 'var(--color-brand)' : 'var(--color-ink-soft)',
            }}
          >
            {entry.label}
          </button>
        ))}
      </nav>

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <Stat
          label={t('growth.grossRevenue')}
          value={<Money amount={data.revenue.gross.amount} currency={currency} />}
          tone="positive"
          hint={`${data.revenue.orders} ${t('admin.orders').toLowerCase()}`}
        />
        <Stat
          label={t('growth.averageOrder')}
          value={<Money amount={data.revenue.averageOrder.amount} currency={currency} />}
        />
        <Stat
          label={t('growth.discountsGiven')}
          value={<Money amount={data.revenue.discounts.amount} currency={currency} />}
        />
        <Stat
          label={t('growth.codOutstanding')}
          value={<Money amount={data.cod.outstanding.amount} currency={currency} />}
          tone={data.cod.outstanding.amount > 0 ? 'warn' : 'neutral'}
        />
      </div>

      {data.revenue.orders === 0 ? (
        <Empty title={t('growth.noData')} />
      ) : (
        <>
          <Panel title={t('growth.grossRevenue')}>
            <RevenueBars series={data.series} currency={currency} />
          </Panel>

          {/*
            COD is its own report, not a filter (§3 `analytics`). The gap
            between "collected" and "remitted" is money the merchant is owed
            by their courier right now — the number that gets forgotten.
          */}
          <Panel title="COD" padded={false}>
            <Rows>
              <CodRow label={t('growth.codShipped')} money={data.cod.shipped} />
              <CodRow label={t('growth.codCollected')} money={data.cod.collected} />
              <CodRow label={t('growth.codRemitted')} money={data.cod.remitted} />
              <CodRow label={t('growth.codOutstanding')} money={data.cod.outstanding} emphasis />
            </Rows>
          </Panel>

          <Panel title={t('growth.topProducts')} padded={false}>
            {data.topProducts.length === 0 ? (
              <Empty title={t('common.empty')} />
            ) : (
              <Rows>
                {data.topProducts.map((product, index) => (
                  <Row key={`${product.productId ?? 'gone'}-${index}`}>
                    <span className="min-w-0 flex-1 truncate">
                      {product.name[locale] || product.name.en}
                    </span>
                    <span className="text-xs" style={{ color: 'var(--color-ink-soft)' }}>
                      {t('growth.units', { count: product.units })}
                    </span>
                    <Money
                      className="tabular-nums font-medium"
                      amount={product.revenue}
                      currency={currency}
                    />
                  </Row>
                ))}
              </Rows>
            )}
          </Panel>

          <Panel title={t('growth.topVariants')} padded={false}>
            {data.topVariants.length === 0 ? (
              <Empty title={t('common.empty')} />
            ) : (
              <Rows>
                {data.topVariants.map((variant) => (
                  <Row key={variant.label}>
                    <span className="min-w-0 flex-1 truncate">{variant.label}</span>
                    <span className="tabular-nums font-medium">
                      {t('growth.units', { count: variant.units })}
                    </span>
                  </Row>
                ))}
              </Rows>
            )}
          </Panel>

          <Panel title={t('growth.acquisition')} padded={false}>
            <Rows>
              {data.acquisition.map((entry) => (
                <Row key={entry.source}>
                  <span className="min-w-0 flex-1 truncate">{entry.source}</span>
                  <span className="text-xs" style={{ color: 'var(--color-ink-soft)' }}>
                    {entry.orders}
                  </span>
                  <Money
                    className="tabular-nums font-medium"
                    amount={entry.revenue}
                    currency={currency}
                  />
                </Row>
              ))}
            </Rows>
          </Panel>
        </>
      )}
    </div>
  );
}

function CodRow({
  label,
  money,
  emphasis,
}: {
  label: string;
  money: { amount: number; currency: 'USD' | 'KHR' };
  emphasis?: boolean;
}) {
  return (
    <Row>
      <span className="flex-1" style={{ color: emphasis ? undefined : 'var(--color-ink-soft)' }}>
        {label}
      </span>
      <Money
        className={emphasis ? 'font-bold tabular-nums' : 'tabular-nums'}
        amount={money.amount}
        currency={money.currency}
      />
    </Row>
  );
}

/**
 * A bar chart drawn with divs rather than a charting library. Merchants open
 * this on a mid-range Android over mobile data (§7 bundle budget), and 40kB of
 * chart runtime buys nothing that a proportional height does not.
 */
function RevenueBars({
  series,
  currency,
}: {
  series: AnalyticsDto['series'];
  currency: 'USD' | 'KHR';
}) {
  const peak = Math.max(1, ...series.map((point) => point.revenue));

  return (
    <div className="flex h-40 items-end gap-1 overflow-x-auto">
      {series.map((point) => (
        <div key={point.bucket} className="flex min-w-3 flex-1 flex-col items-center gap-1">
          <div
            className="w-full rounded-t"
            style={{
              height: `${Math.max(2, (point.revenue / peak) * 100)}%`,
              background: 'var(--color-brand)',
            }}
            title={`${point.bucket}: ${point.orders}`}
          />
          <span
            className="text-[9px] tabular-nums"
            style={{ color: 'var(--color-ink-faint)' }}
          >
            {point.bucket.slice(5)}
          </span>
        </div>
      ))}
      <span className="sr-only">
        <Money amount={peak} currency={currency} />
      </span>
    </div>
  );
}
